import React, { useEffect, useRef, useState } from 'react';
import {
  PolicyScenario,
  PolicyImpactResults,
  calculatePolicyImpact,
  getDefaultScenario,
  getPredefinedScenarios,
} from '../../services/policyImpactService';
import { getMicromobilityStats, MicromobilityStats } from '../../services/policyImpactService';
import {
  InterventionAreas,
  getDefaultInterventionAreas,
} from '../../types/policyImpact';
import PolicyImpactChart from './PolicyImpactChart';
import ParkingPricingPolicy from './ParkingPricingPolicy';
import VehicleElectrificationPolicy from './VehicleElectrificationPolicy';
import EmissionsPerMileChart from './EmissionsPerMileChart';
import InterventionAreasControl from './InterventionAreasControl';
import { useClimatePlansAdmin } from '../../hooks/useClimatePlansAccess';
import TransitServiceExpansionCharts from './TransitServiceExpansionCharts';
import VMTOverTimeChart from './VMTOverTimeChart';
import { supabaseClient } from '../../services/supabase';
import {
  beyondBauEvSliderToAbsolute,
} from '../../utils/evBeyondBauSlider';

const TRANSIT_ELASTICITY = 0.7;
const TRANSIT_MODE_SHARE_PERCENT = 20;
const TRANSIT_STATEWIDE_MODE_SHIFT_PERCENT = 57.8;
const TRANSIT_IMPLEMENTATION_YEAR = 2025;
const TRANSIT_PROJECTION_END_YEAR = 2045;

const calculateTransitVmtReductionPercent = (serviceIncreasePercent: number): number => {
  const vmtReductionPct =
    TRANSIT_ELASTICITY *
    (serviceIncreasePercent / 100) *
    (TRANSIT_MODE_SHARE_PERCENT / 100) *
    (TRANSIT_STATEWIDE_MODE_SHIFT_PERCENT / 100) *
    100;
  return Math.max(0, Number(vmtReductionPct.toFixed(3)));
};

export interface PolicyPanelProps {
  location: string;
  geofips?: number | null;
  emfacVersion: '2021' | '2025';
  activeChart: 'line' | 'contributions' | 'forecast' | 'stacked';
  targetReductionPercent: number;
  targetBaselineYear: number;
  panelLabel?: string;
  compact?: boolean;
  perCapitaMode?: boolean;
}

const PolicyPanel: React.FC<PolicyPanelProps> = ({
  location,
  geofips = null,
  emfacVersion,
  activeChart,
  targetReductionPercent,
  targetBaselineYear,
  panelLabel,
  compact = false,
  perCapitaMode = false,
}) => {
  const [scenario, setScenario] = useState<PolicyScenario>(getDefaultScenario());
  const [results, setResults] = useState<PolicyImpactResults | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [interventionAreas, setInterventionAreas] = useState<InterventionAreas>(getDefaultInterventionAreas());
  const [stackedTotals, setStackedTotals] = useState<null | {
    baselineYear: number;
    baselineTotal: number;
    projectedYear: number;
    projectedWithPoliciesTotal: number;
    totalReduction: number;
    percentReduction: number;
  }>(null);
  const [expandedIntervention, setExpandedIntervention] = useState<keyof InterventionAreas | null>(null);
  const [expandedSubPolicy, setExpandedSubPolicy] = useState<string | null>(null);
  const { isAdmin: canExpandDetails } = useClimatePlansAdmin();

  useEffect(() => {
    if (canExpandDetails) return;
    setExpandedIntervention(null);
    setExpandedSubPolicy(null);
  }, [canExpandDetails]);
  const [activeScenarioName, setActiveScenarioName] = useState<string | null>(null);
  const scenarioLoadRef = useRef(false);
  const [subPolicyValues, setSubPolicyValues] = useState({
    transitOrientedDevelopment: 0,
    downtownHomesPercent: 70,
    activeTransportation: 0,
    transitImprovements: 0,
    parkingPricing: 0,
    micromobility: 0,
    otherInitiatives: 0,
  });
  const [micromobilityStats, setMicromobilityStats] = useState<MicromobilityStats | null>(null);
  const [micromobilityVMTReductionPercent, setMicromobilityVMTReductionPercent] = useState<number>(0);
  const [transitRevenueMiles, setTransitRevenueMiles] = useState<{ year: number; miles: number } | null>(null);
  const [transitServiceHistory, setTransitServiceHistory] = useState<Array<{ year: number; miles: number }>>([]);
  const [transitVMTReductionPercent, setTransitVMTReductionPercent] = useState<number>(0);

  const computeParkingVMTReductionPercent = (assumptions: PolicyScenario['parkingPricing']): number => {
    if (assumptions.priceIncreasePercent === 0) return 0;
    const SF_COMMUTE_BY_CAR_PERCENT = 30.29;
    const BASE_ELASTICITY = -0.4;
    if (assumptions.commuteByCarPercent <= 0 || assumptions.commuteByCarPercent >= 100) return 0;
    const elasticity = BASE_ELASTICITY * (SF_COMMUTE_BY_CAR_PERCENT / assumptions.commuteByCarPercent);
    const vmtReductionPercent = Math.abs(elasticity) * (assumptions.priceIncreasePercent / 100);
    const effective = vmtReductionPercent * (assumptions.vmtAffectedPercent / 100) * 100;
    return Math.max(0, Math.min(20, Number(effective.toFixed(1))));
  };

  useEffect(() => {
    setScenario(prev => ({
      ...prev,
      parkingPricing: { ...prev.parkingPricing, priceIncreasePercent: subPolicyValues.parkingPricing },
    }));
  }, [subPolicyValues.parkingPricing]);

  const parkingVMTReductionPercent = computeParkingVMTReductionPercent(scenario.parkingPricing);

  useEffect(() => {
    if (scenarioLoadRef.current) { scenarioLoadRef.current = false; return; }
    const currentParkingVMT = computeParkingVMTReductionPercent({
      ...scenario.parkingPricing, priceIncreasePercent: subPolicyValues.parkingPricing,
    });
    const sumOfPolicyReductions =
      (subPolicyValues.transitOrientedDevelopment || 0) +
      (subPolicyValues.activeTransportation || 0) +
      transitVMTReductionPercent + currentParkingVMT + micromobilityVMTReductionPercent;
    if (expandedIntervention === 'vmtReduction') {
      setInterventionAreas(prev => {
        const currentTotal = prev.vmtReduction;
        const oi = currentTotal - sumOfPolicyReductions;
        if (oi <= 0) {
          const newTotal = Math.max(0, Math.min(20, sumOfPolicyReductions));
          if (Math.abs(currentTotal - newTotal) > 0.01) return { ...prev, vmtReduction: newTotal };
        }
        return prev;
      });
      return;
    }
    const newTotal = Math.max(0, Math.min(20, sumOfPolicyReductions));
    setInterventionAreas(prev => {
      if (Math.abs(prev.vmtReduction - newTotal) > 0.01) return { ...prev, vmtReduction: newTotal };
      return prev;
    });
  }, [scenario.parkingPricing, subPolicyValues.parkingPricing, transitVMTReductionPercent, micromobilityVMTReductionPercent, subPolicyValues.transitOrientedDevelopment, subPolicyValues.activeTransportation, expandedIntervention]);

  useEffect(() => {
    setScenario(prev => ({ ...prev, vmtReductionPercent: interventionAreas.vmtReduction }));
  }, [interventionAreas.vmtReduction]);

  useEffect(() => {
    const currentParkingVMT = computeParkingVMTReductionPercent(scenario.parkingPricing);
    const sumOfPolicyReductions =
      (subPolicyValues.transitOrientedDevelopment || 0) +
      (subPolicyValues.activeTransportation || 0) +
      transitVMTReductionPercent + currentParkingVMT + micromobilityVMTReductionPercent;
    const calculatedOI = Math.max(0, interventionAreas.vmtReduction - sumOfPolicyReductions);
    setSubPolicyValues(prev => {
      if (Math.abs((prev.otherInitiatives || 0) - calculatedOI) > 0.01) return { ...prev, otherInitiatives: calculatedOI };
      return prev;
    });
  }, [interventionAreas.vmtReduction, subPolicyValues.transitOrientedDevelopment, subPolicyValues.activeTransportation, scenario.parkingPricing, transitVMTReductionPercent, micromobilityVMTReductionPercent]);

  useEffect(() => {
    setSubPolicyValues(prev => ({
      ...prev,
      transitOrientedDevelopment: scenario.vmtReductionComponents.transitOrientedDevelopment,
      activeTransportation: scenario.vmtReductionComponents.activeTransportation,
    }));
  }, [scenario.vmtReductionComponents.transitOrientedDevelopment, scenario.vmtReductionComponents.activeTransportation]);

  useEffect(() => {
    setScenario(prev => ({
      ...prev,
      vmtReductionComponents: {
        ...prev.vmtReductionComponents,
        transitOrientedDevelopment: subPolicyValues.transitOrientedDevelopment,
        activeTransportation: subPolicyValues.activeTransportation,
      },
    }));
  }, [subPolicyValues.transitOrientedDevelopment, subPolicyValues.activeTransportation]);

  useEffect(() => {
    setScenario(prev => ({
      ...prev,
      micromobility: {
        ...(prev.micromobility || {
          yearImplemented: 2025, tripsPerResident: 3, percentBike: 60, percentScooter: 40,
          milesPerBikeTrip: 1.61, milesPerScooterTrip: 1.2,
          percentBikeTripsDisplaceCars: 30, percentScooterTripsDisplaceCars: 20,
          kwhPerMileBike: 0.02, kwhPerMileScooter: 0.04, scalePercent: 0,
        }),
        scalePercent: subPolicyValues.micromobility || 0,
      },
    }));
  }, [subPolicyValues.micromobility]);

  useEffect(() => {
    const increasePct = subPolicyValues.transitImprovements || 0;
    const pctRounded = calculateTransitVmtReductionPercent(increasePct);
    setTransitVMTReductionPercent(pctRounded);
    setScenario(prev => ({
      ...prev,
      vmtReductionComponents: { ...prev.vmtReductionComponents, transitImprovements: pctRounded },
    }));
  }, [subPolicyValues.transitImprovements]);

  useEffect(() => {
    const loadStats = async () => {
      if (!location) return;
      try {
        const stats = await getMicromobilityStats(location, {
          ...scenario,
          micromobility: {
            ...(scenario.micromobility || {
              yearImplemented: 2025, tripsPerResident: 3, percentBike: 60, percentScooter: 40,
              milesPerBikeTrip: 1.61, milesPerScooterTrip: 1.2,
              percentBikeTripsDisplaceCars: 30, percentScooterTripsDisplaceCars: 20,
              kwhPerMileBike: 0.02, kwhPerMileScooter: 0.04, scalePercent: 0,
            }),
            scalePercent: subPolicyValues.micromobility || 0,
          },
        } as PolicyScenario, emfacVersion);
        setMicromobilityStats(stats);
        if (stats.displacedCarMiles > 0 && results?.baseline?.vmt) {
          const pct = (stats.displacedCarMiles / results.baseline.vmt) * 100;
          setMicromobilityVMTReductionPercent(Math.max(0, Math.min(20, Number(pct.toFixed(1)))));
        } else {
          setMicromobilityVMTReductionPercent(0);
        }
      } catch {
        setMicromobilityStats(null);
        setMicromobilityVMTReductionPercent(0);
      }
    };
    if (results?.baseline?.vmt) loadStats();
  }, [location, scenario, subPolicyValues.micromobility, results?.baseline?.vmt, emfacVersion]);

  useEffect(() => {
    const loadTransitMiles = async () => {
      try {
        let row: Record<string, unknown> | null = null;
        if (geofips) {
          const { data } = await supabaseClient.from('vehicle_revenue_miles').select('*').eq('geofips', String(geofips)).maybeSingle();
          row = (data as Record<string, unknown> | null) || null;
        }
        if (!row) {
          const { data } = await supabaseClient.from('vehicle_revenue_miles').select('*').ilike('geo_name', `%${location}%`).in('location_type', ['City', 'Place']).limit(1).maybeSingle();
          row = (data as Record<string, unknown> | null) || null;
        }
        if (row) {
          const history = Object.entries(row)
            .map(([key, rawValue]) => {
              if (!/^\d{4}$/.test(key)) return null;
              const year = Number(key);
              const miles = Number(String(rawValue ?? '').replace(/,/g, ''));
              if (!Number.isFinite(year) || !Number.isFinite(miles) || miles <= 0) return null;
              return { year, miles };
            })
            .filter((point): point is { year: number; miles: number } => point !== null)
            .sort((a, b) => a.year - b.year);
          setTransitServiceHistory(history);
          const latestThrough2023 = [...history].reverse().find((p) => p.year <= 2023);
          const latestAvailable = latestThrough2023 || history[history.length - 1];
          if (latestAvailable) { setTransitRevenueMiles(latestAvailable); return; }
        }
        setTransitServiceHistory([]);
        setTransitRevenueMiles(null);
      } catch {
        setTransitServiceHistory([]);
        setTransitRevenueMiles(null);
      }
    };
    loadTransitMiles();
  }, [location, geofips]);

  // Sync beyond-BAU EV slider → absolute fleet EV% (no absolute floor on the UI lever).
  useEffect(() => {
    const bau2045 = results?.baseline.currentEVPercent;
    if (bau2045 === undefined || !Number.isFinite(bau2045)) return;
    const absolute = beyondBauEvSliderToAbsolute(interventionAreas.vehicleElectrification, bau2045);
    setScenario((prev) =>
      Math.abs(prev.evAdoptionPercent - absolute) < 1e-6 ? prev : { ...prev, evAdoptionPercent: absolute }
    );
  }, [interventionAreas.vehicleElectrification, results?.baseline.currentEVPercent]);

  useEffect(() => {
    setInterventionAreas(prev => ({ ...prev, gridDecarbonization: prev.gridDecarbonization || 2045 }));
    setScenario(prev => ({ ...prev, gridCleanElectricityYear: (prev as any).gridCleanElectricityYear || 2045 } as any));
  }, []);

  useEffect(() => {
    setScenario(prev => ({ ...prev, energyEfficiencyPercent: Math.max(0, Math.min(40, interventionAreas.energyEfficiency || 0)) }));
  }, [interventionAreas.energyEfficiency]);

  useEffect(() => {
    setScenario(prev => ({ ...prev, heatPumpAdoptionPercent: Math.max(0, Math.min(100, interventionAreas.buildingsElectrification || 0)) }));
  }, [interventionAreas.buildingsElectrification]);

  useEffect(() => {
    setScenario(prev => ({ ...prev, organicsDiversionPercent: Math.max(50, Math.min(100, interventionAreas.organicsDiversion || 50)) }));
  }, [interventionAreas.organicsDiversion]);

  useEffect(() => {
    const raw = Number(interventionAreas.gridDecarbonization || 2045);
    setScenario(prev => ({ ...prev, gridCleanElectricityYear: Math.max(2025, Math.min(2045, raw)) } as any));
  }, [interventionAreas.gridDecarbonization]);

  const calculatingRef = useRef(false);

  useEffect(() => {
    const calculateImpact = async () => {
      if (!location) return;
      if (calculatingRef.current) return;
      calculatingRef.current = true;
      setLoading(true);
      setError(null);
      try {
        const impact = await calculatePolicyImpact(location, scenario, emfacVersion);
        setResults(impact);
      } catch (err) {
        console.error('Error calculating policy impact:', err);
        setError('Failed to calculate policy impact. Please try again.');
      } finally {
        setLoading(false);
        calculatingRef.current = false;
      }
    };
    const timeoutId = setTimeout(calculateImpact, 500);
    return () => { clearTimeout(timeoutId); calculatingRef.current = false; };
  }, [location, scenario, emfacVersion]);

  const handleLoadPredefinedScenario = (scenarioName: string) => {
    const predefined = getPredefinedScenarios();
    if (predefined[scenarioName]) {
      const loadedScenario = { ...predefined[scenarioName] };
      if (scenarioName === 'electrificationFocus' && emfacVersion === '2021') {
        loadedScenario.evAdoptionPercent = 70;
      }
      // Preset EV values are beyond-BAU (0=BAU … 100=100% ZEV).
      const beyondBauEv = Math.max(0, Math.min(100, loadedScenario.evAdoptionPercent));
      const bau2045 = results?.baseline.currentEVPercent;
      const absoluteEv =
        bau2045 != null && Number.isFinite(bau2045)
          ? beyondBauEvSliderToAbsolute(beyondBauEv, bau2045)
          : beyondBauEv;
      scenarioLoadRef.current = true;
      setScenario({ ...loadedScenario, evAdoptionPercent: absoluteEv });
      setActiveScenarioName(scenarioName);
      setInterventionAreas({
        urbanInfill: 0,
        vmtReduction: loadedScenario.vmtReductionPercent,
        vehicleElectrification: beyondBauEv,
        energyEfficiency:
          loadedScenario.energyEfficiencyTargetPercent ?? loadedScenario.energyEfficiencyPercent ?? 0,
        buildingsElectrification: loadedScenario.heatPumpAdoptionPercent,
        gridDecarbonization: loadedScenario.gridCleanElectricityYear,
        organicsDiversion: loadedScenario.organicsDiversionPercent,
      });
      const subPolicySum =
        (loadedScenario.vmtReductionComponents.transitOrientedDevelopment || 0) +
        (loadedScenario.vmtReductionComponents.activeTransportation || 0);
      const otherInit = Math.max(0, loadedScenario.vmtReductionPercent - subPolicySum);
      setSubPolicyValues(prev => ({
        ...prev,
        transitOrientedDevelopment: loadedScenario.vmtReductionComponents.transitOrientedDevelopment,
        downtownHomesPercent: loadedScenario.downtownHomesPercent ?? 70,
        activeTransportation: loadedScenario.vmtReductionComponents.activeTransportation,
        transitImprovements: loadedScenario.vmtReductionComponents.transitImprovements,
        parkingPricing: loadedScenario.parkingPricing.priceIncreasePercent,
        micromobility: loadedScenario.micromobility?.scalePercent || 0,
        otherInitiatives: otherInit,
      }));
    }
  };

  const handleResetScenario = () => {
    scenarioLoadRef.current = true;
    const defaultScenario = getDefaultScenario();
    const bau2045 = results?.baseline.currentEVPercent;
    setScenario({
      ...defaultScenario,
      evAdoptionPercent: bau2045 != null && Number.isFinite(bau2045) ? bau2045 : 0,
    });
    setActiveScenarioName(null);
    setInterventionAreas({
      urbanInfill: 0,
      vmtReduction: defaultScenario.vmtReductionPercent,
      vehicleElectrification: 0,
      energyEfficiency:
        defaultScenario.energyEfficiencyTargetPercent ?? defaultScenario.energyEfficiencyPercent ?? 0,
      buildingsElectrification: defaultScenario.heatPumpAdoptionPercent,
      gridDecarbonization: defaultScenario.gridCleanElectricityYear,
      organicsDiversion: defaultScenario.organicsDiversionPercent,
    });
    setSubPolicyValues({
      transitOrientedDevelopment: defaultScenario.vmtReductionComponents.transitOrientedDevelopment,
      downtownHomesPercent: defaultScenario.downtownHomesPercent ?? 70,
      activeTransportation: defaultScenario.vmtReductionComponents.activeTransportation,
      transitImprovements: defaultScenario.vmtReductionComponents.transitImprovements,
      parkingPricing: defaultScenario.parkingPricing.priceIncreasePercent,
      micromobility: defaultScenario.micromobility?.scalePercent || 0,
      otherInitiatives: 0,
    });
  };

  return (
    <div className={`policy-panel${compact ? ' policy-panel-compact' : ''}`}>
      {panelLabel && <h3 className="policy-panel-label">{panelLabel}</h3>}

      {error && <div className="policy-tool-error"><p>{error}</p></div>}

      {/* Metrics */}
      <div className="policy-main-forecast-layout">
        {results && (
          <div className="policy-main-metrics">
            <div className="impact-metric-card baseline">
              <div className="metric-label">Current Baseline</div>
              <div className="metric-value">
                <span className="metric-number">{(stackedTotals?.baselineTotal ?? results.baseline.totalGHG).toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                <span className="metric-unit">MT CO₂e</span>
              </div>
            </div>
            <div className="impact-metric-card projected">
              <div className="metric-label">Projected with Policies</div>
              <div className="metric-value">
                <span className="metric-number">{(stackedTotals?.projectedWithPoliciesTotal ?? results.projected.totalGHG).toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                <span className="metric-unit">MT CO₂e</span>
              </div>
            </div>
            <div className="impact-metric-card reduction">
              <div className="metric-label">Total Reduction</div>
              <div className="metric-value reduction-value">
                <span className="metric-number">{(stackedTotals?.totalReduction ?? results.reductions.totalReduction).toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                <span className="metric-unit">MT CO₂e</span>
              </div>
            </div>
            <div className="impact-metric-card percentage">
              <div className="metric-label">Percent Reduction</div>
              <div className="metric-value reduction-value">
                <span className="metric-number">{(stackedTotals?.percentReduction ?? results.reductions.percentReduction).toFixed(1)}%</span>
              </div>
            </div>
          </div>
        )}
        <div className="policy-main-chart">
          <PolicyImpactChart
            results={results} loading={loading} scenario={scenario} location={location}
            chartType={activeChart} targetReductionPercent={targetReductionPercent}
            targetBaselineYear={targetBaselineYear} emfacVersion={emfacVersion}
            perCapitaMode={perCapitaMode}
            onTotalsChange={setStackedTotals}
          />
        </div>
      </div>

      {/* Intervention sliders */}
      <div className="policy-intervention-areas-section">
        <InterventionAreasControl
          interventionAreas={interventionAreas}
          onChange={setInterventionAreas}
          onExpandedChange={setExpandedIntervention}
          onSubPolicyExpand={setExpandedSubPolicy}
          subPolicyValues={subPolicyValues}
          onSubPolicyChange={setSubPolicyValues}
          parkingVMTReductionPercent={parkingVMTReductionPercent}
          computeParkingVMTReductionPercent={(priceIncreasePercent: number) =>
            computeParkingVMTReductionPercent({ ...scenario.parkingPricing, priceIncreasePercent })
          }
          micromobilityVMTReductionPercent={micromobilityVMTReductionPercent}
          transitVMTReductionPercent={transitVMTReductionPercent}
          vehicleElectrificationBau2045Percent={results?.baseline.currentEVPercent}
          onLoadScenario={handleLoadPredefinedScenario}
          onResetScenario={handleResetScenario}
          activeScenarioName={activeScenarioName}
          onClearScenarioName={() => setActiveScenarioName(null)}
        />
      </div>

      {/* VMT over time chart */}
      {canExpandDetails && expandedIntervention === 'vmtReduction' && !expandedSubPolicy && (
        <div className="policy-intervention-details-section vmt-over-time-section">
          <VMTOverTimeChart
            location={location} vmtReductionPercent={interventionAreas.vmtReduction}
            geofips={geofips} emfacVersion={emfacVersion}
          />
        </div>
      )}

      {/* Expanded sub-policy details */}
      {canExpandDetails && expandedIntervention === 'vmtReduction' && expandedSubPolicy === 'parkingPricing' && (
        <div id="parking-pricing-details" className="policy-intervention-details-section">
          <h3 className="intervention-details-title">Parking Pricing Policy</h3>
          <div className="intervention-details-content">
            <ParkingPricingPolicy
              assumptions={{ ...scenario.parkingPricing, priceIncreasePercent: subPolicyValues.parkingPricing }}
              onChange={(updatedAssumptions) => {
                setScenario(prev => ({ ...prev, parkingPricing: updatedAssumptions }));
                setSubPolicyValues(prev => ({ ...prev, parkingPricing: updatedAssumptions.priceIncreasePercent }));
                const computedParking = computeParkingVMTReductionPercent(updatedAssumptions);
                const sum =
                  (subPolicyValues.transitOrientedDevelopment || 0) +
                  (subPolicyValues.activeTransportation || 0) +
                  transitVMTReductionPercent + computedParking + micromobilityVMTReductionPercent;
                setInterventionAreas(prev => ({
                  ...prev,
                  vmtReduction: Math.max(0, Math.min(20, sum + (subPolicyValues.otherInitiatives || 0))),
                }));
              }}
              currentVMT={results?.baseline.vmt || 0}
              projectedBAUVMT={results?.baseline.vmt || 0}
              location={location} geofips={geofips}
            />
          </div>
        </div>
      )}

      {canExpandDetails && expandedIntervention === 'vmtReduction' && expandedSubPolicy === 'micromobility' && (
        <div id="micromobility-details" className="policy-intervention-details-section" style={{ marginTop: '32px' }}>
          <h3 className="intervention-details-title">Expand Micromobility</h3>
          <div className="intervention-details-content">
            <div className="policy-main-control-section" style={{ marginBottom: 16 }}>
              <div className="policy-main-label">Expand micromobility by</div>
              <div className="policy-main-slider-container">
                <input type="range" min={0} max={200} step={5} value={subPolicyValues.micromobility}
                  onChange={(e) => setSubPolicyValues(prev => ({ ...prev, micromobility: Number(e.target.value) }))}
                  className="policy-main-slider" />
                <div className="policy-main-value">{subPolicyValues.micromobility}%</div>
              </div>
            </div>
            <p>
              This measure estimates reduced GHG emissions from trips shifted to shared e-bikes and e-scooters and
              subtracts added electricity emissions. Default assumptions reflect findings that roughly one-third of
              micromobility trips displace motor vehicle trips.
            </p>
            {micromobilityStats && (
              <div className="policy-results-grid">
                <div className="result-item"><span className="result-label">Population:</span><span className="result-value">{micromobilityStats.population.toLocaleString()}</span></div>
                <div className="result-item"><span className="result-label">Program Scale:</span><span className="result-value">{micromobilityStats.scalePercent}%</span></div>
                <div className="result-item"><span className="result-label">Trips per Resident:</span><span className="result-value">{micromobilityStats.tripsPerResident}</span></div>
                <div className="result-item"><span className="result-label">Total Program Trips:</span><span className="result-value">{micromobilityStats.totalProgramTrips.toLocaleString()}</span></div>
                <div className="result-item"><span className="result-label">Annual Bike Miles:</span><span className="result-value">{micromobilityStats.bikeMiles.toLocaleString()}</span></div>
                <div className="result-item"><span className="result-label">Annual Scooter Miles:</span><span className="result-value">{micromobilityStats.scooterMiles.toLocaleString()}</span></div>
                <div className="result-item highlight"><span className="result-label">VMT Reduced:</span><span className="result-value">{micromobilityStats.displacedCarMiles.toLocaleString()} miles</span></div>
                <div className="result-item"><span className="result-label">EV Bike+Scooter Electricity:</span><span className="result-value">{micromobilityStats.electricityKwh.toLocaleString()} kWh</span></div>
                <div className="result-item"><span className="result-label">Tons Avoided from Cars:</span><span className="result-value">{micromobilityStats.avoidedCarTons.toLocaleString()}</span></div>
                <div className="result-item"><span className="result-label">Tons Added from Electricity:</span><span className="result-value">{micromobilityStats.electricityTons.toLocaleString()}</span></div>
                <div className="result-item highlight"><span className="result-label">Net GHG Reduction:</span><span className="result-value">{micromobilityStats.netTons.toLocaleString()} MT CO₂e</span></div>
              </div>
            )}
            {!micromobilityStats && <p style={{ opacity: 0.7 }}>Loading micromobility statistics…</p>}
          </div>
        </div>
      )}

      {canExpandDetails && expandedIntervention === 'vmtReduction' && expandedSubPolicy === 'activeTransportation' && (
        <div id="active-transportation-details" className="policy-intervention-details-section" style={{ marginTop: '32px' }}>
          <h3 className="intervention-details-title">Active transportation infrastructure (Under construction)</h3>
          <div className="intervention-details-content">
            <p
              style={{
                margin: '0 0 1rem',
                padding: '0.75rem 1rem',
                borderRadius: 8,
                background: 'var(--section-bg, #f8fafc)',
                border: '1px solid var(--border-color, #e2e8f0)',
                color: 'var(--text-secondary, #64748b)',
                fontSize: '0.9375rem',
                lineHeight: 1.5,
              }}
            >
              Detailed maps and documentation for this policy are under construction. Adjust{' '}
              <strong>Expand active transportation infrastructure</strong> in the policy menu; those values still feed
              the GHG forecast for this panel.
            </p>
          </div>
        </div>
      )}

      {canExpandDetails && expandedIntervention === 'vehicleElectrification' && (
        <div id="vehicle-electrification-details" className="policy-intervention-details-section">
          <h3 className="intervention-details-title">Vehicle Electrification Policy</h3>
          <div className="intervention-details-content">
            <VehicleElectrificationPolicy
              emfacVersion={emfacVersion}
              currentEVPercent={results?.baseline.currentEVPercent || 0}
              currentEVPercent2023={results?.baseline.currentEVPercent2023}
              beyondBauPercent={interventionAreas.vehicleElectrification}
              targetEVPercent={beyondBauEvSliderToAbsolute(
                interventionAreas.vehicleElectrification,
                results?.baseline.currentEVPercent || 0
              )}
              location={location} results={results}
            />
            <EmissionsPerMileChart
              results={results}
              currentEVPercent={results?.baseline.currentEVPercent ?? 0}
              currentEVPercent2023={results?.baseline.currentEVPercent2023}
              targetEVPercent={beyondBauEvSliderToAbsolute(
                interventionAreas.vehicleElectrification,
                results?.baseline.currentEVPercent || 0
              )}
            />
          </div>
        </div>
      )}

      {canExpandDetails && expandedIntervention === 'vmtReduction' && expandedSubPolicy === 'transitImprovements' && (
        <div id="transit-service-details" className="policy-intervention-details-section" style={{ marginTop: '32px' }}>
          <h3 className="intervention-details-title">Extend Transit Network Coverage of Miles</h3>
          <div className="intervention-details-content">
            <div className="policy-main-control-section" style={{ marginBottom: 16 }}>
              <div className="policy-main-label">Expand transit service by</div>
              <div className="policy-main-slider-container">
                <input type="range" min={0} max={50} step={1} value={subPolicyValues.transitImprovements}
                  onChange={(e) => setSubPolicyValues(prev => ({ ...prev, transitImprovements: Number(e.target.value) }))}
                  className="policy-main-slider" />
                <div className="policy-main-value">{subPolicyValues.transitImprovements}%</div>
              </div>
            </div>
            <p>
              This measure will expand the local transit network by either adding or modifying existing service or
              extending operating hours near the project area.
            </p>
            {(() => {
              const percentIncrease = subPolicyValues.transitImprovements || 0;
              const vmtReductionPctRounded = calculateTransitVmtReductionPercent(percentIncrease);
              const yearsTo2045 = TRANSIT_PROJECTION_END_YEAR - TRANSIT_IMPLEMENTATION_YEAR + 1;
              const ldvBaseline = results?.baseline.lightDutyGHG || 0;
              const estGHGReduction2045 = Math.round(ldvBaseline * yearsTo2045 * (vmtReductionPctRounded / 100));
              const baselineServiceMiles = transitRevenueMiles?.miles;
              const baselineYear = transitRevenueMiles?.year;
              const totalVmt = results?.baseline.vmt || 0;
              const vmtReducedMiles = Math.round(totalVmt * (vmtReductionPctRounded / 100));
              return (
                <div>
                  <p style={{ marginTop: 8 }}>
                    <strong>Smart narrative:</strong> {location}
                    {baselineServiceMiles ? ` has ${baselineServiceMiles.toLocaleString()} transit service miles in ${baselineYear}.` : ''}
                    {` If ${location} increases transit service miles by ${percentIncrease}%, total GHG emissions from light-duty vehicles by 2045 are estimated to be reduced by ${estGHGReduction2045.toLocaleString()} tCO₂e.`}
                  </p>
                  <div className="policy-results-grid" style={{ marginTop: 12 }}>
                    <div className="result-item"><span className="result-label">Elasticity:</span><span className="result-value">{TRANSIT_ELASTICITY}</span></div>
                    <div className="result-item"><span className="result-label">Transit mode share:</span><span className="result-value">{TRANSIT_MODE_SHARE_PERCENT}%</span></div>
                    <div className="result-item"><span className="result-label">Statewide mode shift factor:</span><span className="result-value">{TRANSIT_STATEWIDE_MODE_SHIFT_PERCENT}%</span></div>
                    <div className="result-item highlight"><span className="result-label">VMT reduction in year of implementation:</span><span className="result-value">{vmtReductionPctRounded.toFixed(3)}%</span></div>
                    <div className="result-item highlight"><span className="result-label">Total VMT reduced (year of implementation):</span><span className="result-value">{vmtReducedMiles.toLocaleString()} miles</span></div>
                  </div>
                  <p style={{ marginTop: 8, opacity: 0.75 }}>Sources: FHWA 2017; Handy et al. 2013.</p>
                  <TransitServiceExpansionCharts
                    location={location} historicalServiceMiles={transitServiceHistory}
                    percentIncrease={percentIncrease} baselineServiceMiles={baselineServiceMiles}
                    baselineYear={baselineYear} implementationYear={TRANSIT_IMPLEMENTATION_YEAR}
                    projectionEndYear={TRANSIT_PROJECTION_END_YEAR}
                  />
                </div>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
};

export default PolicyPanel;
